import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { htmlToTextLines } from '@/app/util/formatting_functions';

const PLACEHOLDER_REGEX = /\[[^[\]\n]+\]/g;
const PLUGIN_KEY = new PluginKey('aiPlaceholders');
const DECORATION_CLASS =
  'rounded-sm bg-warning-100 text-warning-900 cursor-pointer hover:bg-warning-200 ' +
  'motion-safe:transition-colors motion-safe:duration-150 motion-safe:ease-bounce';

function placeholderRanges(doc, placeholders) {
  const ranges = [];
  if (placeholders.length === 0) return ranges;

  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    // One character per inline leaf (a mention, a line break), so an index in the text is an offset in the node.
    const text = node.textBetween(0, node.content.size, undefined, '￼');
    placeholders.forEach((placeholder) => {
      let index = text.indexOf(placeholder);
      while (index !== -1) {
        ranges.push({ from: pos + 1 + index, to: pos + 1 + index + placeholder.length });
        index = text.indexOf(placeholder, index + placeholder.length);
      }
    });
    return false;
  });

  return ranges.sort((a, b) => a.from - b.from);
}

function selectRange(editor, range) {
  editor.chain().focus().setTextSelection(range).run();
  return true;
}

function selectAdjacentPlaceholder(editor, placeholders, backwards) {
  const ranges = placeholderRanges(editor.state.doc, placeholders);
  const { from, to } = editor.state.selection;
  const range = backwards
    ? ranges.findLast((candidate) => candidate.to <= from)
    : ranges.find((candidate) => candidate.from >= to);

  return range ? selectRange(editor, range) : false;
}

export function extractAiPlaceholders(text) {
  if (!text) return [];
  return [...new Set(text.match(PLACEHOLDER_REGEX) || [])];
}

export function aiPlaceholdersPresent(placeholders, text) {
  return placeholders.filter((placeholder) => text.includes(placeholder));
}

export function countAiPlaceholders(placeholders, content_html) {
  if (placeholders.length === 0) return 0;
  const text = htmlToTextLines(content_html);
  return placeholders.reduce((count, placeholder) => count + text.split(placeholder).length - 1, 0);
}

export const AiPlaceholders = Extension.create({
  name: 'aiPlaceholders',
  // Above the list extensions, so Tab jumps between placeholders before it indents a list item.
  priority: 1000,

  addOptions() {
    return { placeholders: [] };
  },

  addStorage() {
    return { placeholders: this.options.placeholders };
  },

  addCommands() {
    return {
      setAiPlaceholders:
        (placeholders) =>
        ({ tr, dispatch }) => {
          this.storage.placeholders = placeholders;
          // The decorations are rebuilt per state, so an empty transaction is what redraws them.
          if (dispatch) dispatch(tr.setMeta(PLUGIN_KEY, true).setMeta('addToHistory', false));
          return true;
        },
      selectFirstAiPlaceholder:
        () =>
        ({ chain, tr }) => {
          const [range] = placeholderRanges(tr.doc, this.storage.placeholders);
          return range ? chain().focus().setTextSelection(range).run() : false;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => selectAdjacentPlaceholder(this.editor, this.storage.placeholders, false),
      'Shift-Tab': () => selectAdjacentPlaceholder(this.editor, this.storage.placeholders, true),
    };
  },

  addProseMirrorPlugins() {
    const { storage } = this;

    return [
      new Plugin({
        key: PLUGIN_KEY,
        props: {
          decorations(state) {
            const decorations = placeholderRanges(state.doc, storage.placeholders).map((range) =>
              Decoration.inline(range.from, range.to, { class: DECORATION_CLASS }),
            );
            return DecorationSet.create(state.doc, decorations);
          },
          handleClick(view, pos) {
            const range = placeholderRanges(view.state.doc, storage.placeholders).find(
              (candidate) => pos > candidate.from && pos < candidate.to,
            );
            if (!range) return false;

            view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, range.from, range.to)));
            return true;
          },
        },
      }),
    ];
  },
});
